import React, { useState, useEffect, useRef } from 'react';
import { Box, Flex, Heading } from '@radix-ui/themes';
import { TaxonomyApi, AudienceRecommendationRecommendationItem } from '@digiseg-labs/api-client';

import ChatInput from './components/ChatInput';
import { useTaxonomy } from './hooks/useTaxonomy';
import ConversationList, { ConversationItem } from './components/ConversationList';

import './App.css';

const App: React.FC = () => {
  const [inputValue, setInputValue] = useState('');
  const [conversation, setConversation] = useState<ConversationItem[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [showHeaders, setShowHeaders] = useState(true);
  const [shouldRenderHeaders, setShouldRenderHeaders] = useState(true);
  const [headerAnimation, setHeaderAnimation] = useState<'fadeInDown' | 'fadeOutUp'>('fadeInDown');
  const [hasSubmittedOnce, setHasSubmittedOnce] = useState(false);
  const { countries, platforms, taxonomy, selectedCountry, selectedPlatform, setSelectedCountry, setSelectedPlatform } = useTaxonomy();
  const [isAtFooter, setIsAtFooter] = useState(false);
  const footerObserverRef = useRef<IntersectionObserver | null>(null);
  const chatEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const footerEl = document.querySelector('footer');
    if (!footerEl) return;

    footerObserverRef.current = new IntersectionObserver(
      ([entry]) => setIsAtFooter(entry.isIntersecting),
      { root: null, threshold: 0 }
    );
    footerObserverRef.current.observe(footerEl);
    return () => footerObserverRef.current?.disconnect();
  }, []);
  
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation]);

  useEffect(() => {
    if (!showHeaders) {
      setHeaderAnimation('fadeOutUp');
      const timer = setTimeout(() => setShouldRenderHeaders(false), 600);
      return () => clearTimeout(timer);
    }
  }, [showHeaders]);

  const handleSubmit = async () => {
    if (!inputValue.trim()) return;
    setShowHeaders(false);
    if (!hasSubmittedOnce) setHasSubmittedOnce(true);
    setConversation(prev => [...prev, { sender: 'user', message: inputValue.trim() }]);
    setConversation(prev => [...prev, { sender: 'ai', message: '', isLoading: true }]);

    try {
      const api = new TaxonomyApi();
      const response = await api.completeAudienceRecommendation({ message_text: inputValue, conversation_id: conversationId ?? undefined });
      if (response.data.data?.conversation_id && !conversationId) setConversationId(response.data.data.conversation_id);
      const message = response.data.data?.message_text || '';
      const recommendations: AudienceRecommendationRecommendationItem[] = response.data.data?.recommendations || [];
      setConversation(prev => {
        const newConv = [...prev];
        const loadingIndex = newConv.findIndex(m => m.sender === 'ai' && m.isLoading);
        if (loadingIndex !== -1) newConv[loadingIndex] = { sender: 'ai', message, recommendations, isLoading: false };
        return newConv;
      });
    } catch (error) {
      console.error(error);
      setConversation(prev => {
        const newConv = [...prev];
        const loadingIndex = newConv.findIndex(m => m.sender === 'ai' && m.isLoading);
        if (loadingIndex !== -1) newConv[loadingIndex] = { sender: 'ai', message: 'Sorry, something went wrong.', recommendations: [], isLoading: false };
        return newConv;
      });
    } finally {
      setInputValue('');
    }
  }

  return (
    <Box className="App-wrapper">
      {!hasSubmittedOnce && (
        <Flex align="center" className="intro-container">
          <Box className="intro-box">
            {shouldRenderHeaders && (
              <div className={headerAnimation}>
                <Heading as="h1" size="7" mb="2" align="center">What product or service are you selling?</Heading>
                <Heading as="h2" size="5" weight="regular" align="center">Find the right audience that suits your needs with our AI.</Heading>
              </div>
            )}
            <ConversationList conversation={conversation} taxonomy={taxonomy} countries={countries} platforms={platforms} selectedCountry={selectedCountry} selectedPlatform={selectedPlatform} onCountrySelected={setSelectedCountry} onPlatformSelected={setSelectedPlatform} />
            <ChatInput inputValue={inputValue} onInputValueChange={setInputValue} onSubmit={handleSubmit} />
          </Box>
        </Flex>
      )}
      {hasSubmittedOnce && (
        <>
          <Box className="chat-box-scroll">
            <Flex className="chat-content">
              <ConversationList conversation={conversation} taxonomy={taxonomy} countries={countries} platforms={platforms} selectedCountry={selectedCountry} selectedPlatform={selectedPlatform} onCountrySelected={setSelectedCountry} onPlatformSelected={setSelectedPlatform} />
              <div ref={chatEndRef} />
            </Flex>
          </Box>
          <Box className={`chat-box-input ${isAtFooter ? 'chat-box-stopped' : ''}`}>
            <Box className="chat-input-inner" m="auto">
              <ChatInput inputValue={inputValue} onInputValueChange={setInputValue} onSubmit={handleSubmit} />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default App;
